import {getTestProps} from '@benchling/js-test-utils';
import {StyleSheet, css} from 'aphrodite';
import React, {useMemo} from 'react';

import {SINGLE_CHART_OUTPUT_STEP_DATA_NAME} from 'analysis-tool/AnalysisStepDataNames';
import {SHARED_MAIN_VIEW_COMPONENT_WITH_MARGIN_STYLES} from 'analysis-tool/AnalysisToolConstants';
import {useIsConsolidatedAddSourceEnabled} from 'analysis-tool/AnalysisToolFeatureGates';
import useMaybeDraftStep from 'analysis-tool/right-sidebar/components/useMaybeDraftStep';
import getDataFrameRenderOverrides from 'analysis-tool/ui/flag-rows/getDataFrameRenderOverrides';
import useRightSidebarItem from 'analysis-tool/ui/hooks/useRightSidebarItem';
import {AnalysisMainViewItemType, MainViewComponentProps} from 'analysis-tool/ui/main-view/MainViewTypes';
import RegressionView from 'analysis-tool/ui/main-view/RegressionView';
import {ActionOrigin} from 'constants/AnalyticsConstants';
import {DataFrameRow} from 'data-frames/DataFrameTypes';
import {DataFrameTableMode} from 'data-frames/table/DataFrameTableConstants';
import {PipelineStepStatusCode, PipelineStepType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {useRolloutFlags} from 'launchdarkly/LaunchDarklyUtil';
import useStepTypeInfos from 'pipelines/hooks/useStepTypeInfosQuery';
import {isStepSucceeded} from 'pipelines/PipelineStepStatusUtils';
import {pipelineStepIsStepType, PipelineStep} from 'pipelines/PipelineTypes';
import {
  getDefaultClientIdForView,
  resolveLineageStep,
  resolveOutputStepData,
} from 'pipelines/PipelineViewUtils';
import {getLegacyStepFromAnalysis} from 'pipelines/StepUtil';
import makeUseGlobalClientState from 'ui-library/hooks/global-client-state/makeUseGlobalClientState';
import {AsyncStatus} from 'ui-library/hooks/useAsyncState';

import getAnalysisDataViewActions from './analysis-adapter/getAnalysisDataViewActions';
import getAnalysisViewMetadataContent from './analysis-adapter/getAnalysisViewMetadataContent';
import handleOnTitleChange, {canEditTitle} from './analysis-adapter/handleOnTitleChange';
import {assertViewExists, getViewForViewReference} from './AnalysisViewUtils';
import getDataFrameColumnHeaderActions from './data-frame-view/getDataFrameColumnHeaderActions';
import getTableViewDisplayOptionsOverrides from './data-frame-view/getTableViewDisplayOptionsOverrides';
import type {TableViewDisplayOptions} from './data-frame-view/getTableViewDisplayOptionsOverrides';
import DataView from './DataView';
import DataViewContext, {PreviewDraftStepData, rowSelectionState} from './DataViewContext';

const setChartPreviewsGlobalState = makeUseGlobalClientState<PreviewDraftStepData | null>(null);

export type AnalysisDataViewAdapterProps = MainViewComponentProps<AnalysisMainViewItemType.DATA_VIEW>;

export default function AnalysisDataViewAdapter({
  analysis,
  onChangeSelectedItem,
  onChangeSelectedRightSidebarItem,
  selectedItem,
  clientStateId,
  ...remainingMainViewComponentProps
}: AnalysisDataViewAdapterProps): JSX.Element {
  const {viewReference, showChips} = selectedItem;
  const stepTypeInfos = useStepTypeInfos();
  const view = getViewForViewReference(analysis, viewReference);
  assertViewExists(view, analysis, viewReference);
  const clientId = getDefaultClientIdForView(view, analysis.steps);
  const [selectedRowIndices, _] = rowSelectionState.useGlobalClientState(clientId);

  const lastNode = view.lineage[view.lineage.length - 1];

  // We need previews to use a less specific client id because we're previewing against a step whose output
  // data changes through configuration changes.
  const viewStepId = view.lineage.length > 0 ? lastNode.step.id : 'EMPTY_ANALYSIS';
  const {draftStep} = useMaybeDraftStep(viewStepId, null);
  const [previewDraftStepData, setPreviewDraftStepData] =
    setChartPreviewsGlobalState.useGlobalClientState(viewStepId);

  const {analysisPromoteViewEnabled} = useRolloutFlags();
  const isConsolidatedAddSourceEnabled = useIsConsolidatedAddSourceEnabled(analysis);
  const {selectedRightSidebarItem} = useRightSidebarItem(clientStateId);
  const contextValue = useMemo(() => {
    const lastNode = view.lineage[view.lineage.length - 1];
    const legacyLastStep = getLegacyStepFromAnalysis(analysis, lastNode.step);
    const lastStep = resolveLineageStep(analysis.steps, lastNode);

    // Extract displayOptions from TABLE_VIEW step config to build render overrides.
    const tableViewDisplayOptions = extractTableViewDisplayOptions(lastStep);

    return {
      actionOrigin: ActionOrigin.ANALYSIS_TOOL,
      actions: getAnalysisDataViewActions(
        analysis,
        clientStateId,
        view,
        viewStepId,
        selectedRowIndices,
        onChangeSelectedItem,
        onChangeSelectedRightSidebarItem,
        selectedRightSidebarItem,
        analysisPromoteViewEnabled !== false,
        isConsolidatedAddSourceEnabled,
        stepTypeInfos
      ),
      metadataContent: getAnalysisViewMetadataContent(analysis, view, stepTypeInfos),
      analysisView: view,
      steps: analysis.steps,
      dataFrameViewOptions: {
        dataFrameRenderOverrides: getDataFrameRenderOverrides(analysis, legacyLastStep),
        getAdditionalRenderOverrides:
          tableViewDisplayOptions != null
            ? (rows: ReadonlyArray<DataFrameRow>, totalRows?: number) =>
                getTableViewDisplayOptionsOverrides(tableViewDisplayOptions, rows, totalRows)
            : undefined,
        headerMenuActions: getDataFrameColumnHeaderActions(analysis, clientStateId, view, {
          onChangeSelectedRightSidebarItem,
          selectedRightSidebarItem,
        }),
        defaultTableMode: showChips ? DataFrameTableMode.RICH : undefined,
      },
      onTitleChange: canEditTitle(analysis, view)
        ? async (newTitle: string) => {
            await handleOnTitleChange(newTitle, analysis, view);
          }
        : undefined,
      previewData: draftStep
        ? {
            draftStep,
            previewDraftStepData: previewDraftStepData ?? {
              chartPreviews: [],
              previewStatus: AsyncStatus.NOT_FETCHED,
            },
            setPreviewDraftStepData,
          }
        : undefined,
    };
  }, [
    analysis,
    clientStateId,
    draftStep,
    onChangeSelectedItem,
    onChangeSelectedRightSidebarItem,
    previewDraftStepData,
    analysisPromoteViewEnabled,
    isConsolidatedAddSourceEnabled,
    selectedRowIndices,
    setPreviewDraftStepData,
    showChips,
    view,
    viewStepId,
    selectedRightSidebarItem,
    stepTypeInfos,
  ]);

  // For backwards compatibility with the regression functionality, since it hasn't been modernized to new views
  // yet, we special case in analyses to show `RegressionView` instead of `DataView`. We show this when the
  // selected output is the chart or when the output step data is null (e.g. the source/unconfigured view),
  // and when the step is SUCCEEDED, WAITING (unconfigured), or AI_PROPOSED. Non-chart regression outputs
  // (model variables, model output) use the normal DataView.
  //
  // TODO(BNCH-135912): Remove regression special casing when regression is modernized to new views.
  const lastStep = resolveLineageStep(analysis.steps, lastNode);
  if (
    lastStep.stepType === PipelineStepType.REGRESSION &&
    (isStepSucceeded(lastStep.statusCode) ||
      lastStep.statusCode === PipelineStepStatusCode.WAITING ||
      lastStep.statusCode === PipelineStepStatusCode.AI_PROPOSED)
  ) {
    const outputStepData =
      lastNode.stepOutputReference != null
        ? resolveOutputStepData(analysis.steps, {
            step: lastNode.step,
            stepOutputReference: lastNode.stepOutputReference,
          })
        : null;
    if (outputStepData == null || outputStepData.name === SINGLE_CHART_OUTPUT_STEP_DATA_NAME) {
      const legacyRegressionStep = getLegacyStepFromAnalysis(analysis, lastNode.step);
      return (
        <RegressionView
          {...remainingMainViewComponentProps}
          analysis={analysis}
          analysisClientStateId={clientStateId}
          clientStateId={clientStateId}
          onChangeSelectedItem={onChangeSelectedItem}
          onChangeSelectedRightSidebarItem={onChangeSelectedRightSidebarItem}
          selectedItem={{type: AnalysisMainViewItemType.REGRESSION, stepId: lastNode.step.id}}
          step={legacyRegressionStep}
        />
      );
    }
  }

  return (
    <div
      className={css(styles.wrapper)}
      {...getTestProps({component: 'AnalysisDataViewAdapter', element: 'root'})}
    >
      <DataViewContext.Provider value={contextValue}>
        <DataView />
      </DataViewContext.Provider>
    </div>
  );
}

/**
 * Extract displayOptions from a TABLE_VIEW v0 step's config, if present.
 */
function extractTableViewDisplayOptions(step: PipelineStep): TableViewDisplayOptions | undefined {
  if (!pipelineStepIsStepType(PipelineStepType.TABLE_VIEW, 0, step)) {
    return undefined;
  }
  if (step.config?.displayOptions == null) {
    return undefined;
  }
  return step.config.displayOptions;
}

const styles = StyleSheet.create({
  wrapper: {
    ...SHARED_MAIN_VIEW_COMPONENT_WITH_MARGIN_STYLES,
    height: SHARED_MAIN_VIEW_COMPONENT_WITH_MARGIN_STYLES.maxHeight,
  },
});
